import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Phone, Mail, MapPin, FileText, Pencil, FolderKanban, DollarSign, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { ContactHistory } from '../components/ContactHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABELS = {
  'lead': 'Lead', 'akceptacja': 'Akceptacja', 'brak_akceptacji': 'Brak akceptacji',
  'oplacone': 'Opłacone', 'dostawa': 'Dostawa', 'w_realizacji': 'W realizacji', 'zakonczone': 'Zakończono'
};
const STATUS_COLORS = {
  'lead': 'bg-blue-500/20 text-blue-400', 'akceptacja': 'bg-yellow-500/20 text-yellow-400',
  'brak_akceptacji': 'bg-red-500/20 text-red-400', 'oplacone': 'bg-emerald-500/20 text-emerald-400',
  'dostawa': 'bg-purple-500/20 text-purple-400', 'w_realizacji': 'bg-orange-500/20 text-orange-400',
  'zakonczone': 'bg-green-500/20 text-green-400'
};

export const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ nazwa: '', email: '', telefon: '', adres: '', notatki: '' });
  const [saving, setSaving] = useState(false);

  const fetchClient = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/clients/${id}`);
      setClient(res.data);
    } catch {
      toast.error('Błąd pobierania danych klienta');
      navigate('/klienci');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchClient(); }, [fetchClient]);

  const openEdit = () => {
    if (!client) return;
    setForm({ nazwa: client.nazwa, email: client.email || '', telefon: client.telefon || '', adres: client.adres || '', notatki: client.notatki || '' });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!form.nazwa.trim()) { toast.error('Nazwa jest wymagana'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/clients/${id}`, form);
      toast.success('Klient zaktualizowany');
      setEditOpen(false);
      fetchClient();
    } catch { toast.error('Błąd zapisu'); }
    finally { setSaving(false); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { const dt = new Date(d); return dt.toLocaleDateString('pl-PL'); } catch { return d; }
  };

  if (loading) return <Layout><div className="p-8 text-[var(--crm-text-secondary)]">Ładowanie...</div></Layout>;
  if (!client) return null;

  const projects = client.projects || [];

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8">
        <Button onClick={() => navigate('/klienci')} data-testid="back-to-clients"
          className="bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border border-[var(--crm-border)] mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />Powrót do klientów
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Info Card */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 lg:col-span-1" data-testid="client-info-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[var(--crm-text-primary)]">{client.nazwa}</h2>
              <Button size="sm" variant="ghost" onClick={openEdit} data-testid="edit-client-detail"
                className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-card-hover)]">
                <Pencil className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-3">
              {client.telefon && (
                <a href={`tel:${client.telefon}`} className="flex items-center gap-3 text-[var(--crm-text-secondary)] hover:text-[#FF6B35] transition-colors">
                  <Phone className="w-4 h-4 text-[#FF6B35] shrink-0" />{client.telefon}
                </a>
              )}
              {client.email && (
                <a href={`mailto:${client.email}`} className="flex items-center gap-3 text-[var(--crm-text-secondary)] hover:text-[#FF6B35] transition-colors">
                  <Mail className="w-4 h-4 text-[#FF6B35] shrink-0" />{client.email}
                </a>
              )}
              {client.adres && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(client.adres)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-start gap-3 text-[var(--crm-text-secondary)] hover:text-[#FF6B35] transition-colors">
                  <MapPin className="w-4 h-4 text-[#FF6B35] shrink-0 mt-0.5" />{client.adres}
                </a>
              )}
              {client.notatki && (
                <div className="pt-3 border-t border-[var(--crm-border)]">
                  <div className="flex items-center gap-2 text-[var(--crm-text-secondary)] text-sm mb-1"><FileText className="w-3.5 h-3.5" />Notatki</div>
                  <p className="text-[var(--crm-text-secondary)] text-sm whitespace-pre-wrap">{client.notatki}</p>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 mt-6 pt-4 border-t border-[var(--crm-border)]">
              <div className="bg-[var(--crm-bg-panel)] rounded-lg p-3 text-center">
                <FolderKanban className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-[var(--crm-text-primary)]">{client.projects_count || 0}</p>
                <p className="text-[var(--crm-text-secondary)] text-xs">Projektów</p>
              </div>
              <div className="bg-[var(--crm-bg-panel)] rounded-lg p-3 text-center">
                <DollarSign className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-[var(--crm-text-primary)]">{((client.total_offer || 0) / 1000).toFixed(0)}k</p>
                <p className="text-[var(--crm-text-secondary)] text-xs">Wartość zł</p>
              </div>
            </div>
          </Card>

          {/* Projects Table */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 lg:col-span-2" data-testid="client-projects-card">
            <h3 className="text-lg font-semibold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-[#FF6B35]" />
              Projekty klienta ({projects.length})
            </h3>
            {projects.length === 0 ? (
              <p className="text-[var(--crm-text-secondary)] text-center py-8">Brak projektów powiązanych z tym klientem</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[var(--crm-border)] hover:bg-transparent">
                      <TableHead className="text-[var(--crm-text-secondary)]">Nazwa</TableHead>
                      <TableHead className="text-[var(--crm-text-secondary)] hidden sm:table-cell">Data</TableHead>
                      <TableHead className="text-[var(--crm-text-secondary)] text-right">Oferta</TableHead>
                      <TableHead className="text-[var(--crm-text-secondary)] text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map(p => (
                      <TableRow key={p.id} className="border-b border-[var(--crm-border)]/50 hover:bg-[var(--crm-bg-card-hover)] cursor-pointer"
                        data-testid={`client-project-${p.id}`}
                        onClick={() => navigate(`/projects/${p.id}`)}>
                        <TableCell className="text-[var(--crm-text-primary)] font-medium">{p.nazwa}</TableCell>
                        <TableCell className="text-[var(--crm-text-secondary)] hidden sm:table-cell">
                          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(p.created_at)}</span>
                        </TableCell>
                        <TableCell className="text-right text-[var(--crm-text-secondary)] font-mono">
                          {(p.total_offer || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || 'bg-slate-500/20 text-[var(--crm-text-secondary)]'}`}>
                            {STATUS_LABELS[p.status] || p.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>

        {/* Contact History */}
        <div className="mt-6">
          <ContactHistory entityType="client" entityId={id} />
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[var(--crm-bg-main)] border-[var(--crm-border)] text-[var(--crm-text-primary)] max-w-lg">
          <DialogHeader><DialogTitle>Edytuj klienta</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Nazwa *</Label>
              <Input value={form.nazwa} onChange={(e) => setForm({...form, nazwa: e.target.value})}
                data-testid="client-detail-form-nazwa" className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Telefon</Label>
                <Input value={form.telefon} onChange={(e) => setForm({...form, telefon: e.target.value})}
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
              </div>
              <div>
                <Label className="text-[var(--crm-text-secondary)]">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Adres</Label>
              <Input value={form.adres} onChange={(e) => setForm({...form, adres: e.target.value})}
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
            <div>
              <Label className="text-[var(--crm-text-secondary)]">Notatki</Label>
              <Textarea value={form.notatki} onChange={(e) => setForm({...form, notatki: e.target.value})}
                rows={3} className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} className="text-[var(--crm-text-secondary)]">Anuluj</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="client-detail-form-save"
              className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white">
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
